// The trainer's mark: a target that walks.
//
// **An instrument, not content**, and deliberately the cheapest one that works.
// The head's two aimable slots need a *gradient* — something that changes as
// the army moves, so a search can learn that closing on it pays. Everything
// else the planner can be pointed at is either derived from the position (where
// the enemy is, where the scrap is) or authored by a level, and an authored
// marker is fixed for the whole game: satisfied once, and it stops teaching.
//
// **It wanders, and an earlier draft rejected exactly that** — so the reversal
// is worth being plain about. The objection was never to the walk, it was to
// what a walk was assumed to cost: a field in `Position`, a place in the hash,
// a salt of its own, and a rule for what happens when it is boxed in. Against
// that, the middle of the board cost nothing.
//
// A *derived* walk pays none of it. The mark is a pure function of the seed and
// `fullmove` — recomputed, never stored — so there is no field, nothing to
// hash, and no new state to keep in step across a replay. The boxed-in rule
// turns out to be one line, because the step is chosen from the neighbours that
// are on the board rather than clamped after the fact. What is left is the cost
// of *computing* it in the hottest function the planner has, and that is what
// the memo below is for.
//
// What the reversal buys is the thing the centre could not teach. A fixed
// middle is satisfiable by learning "the middle": an army that walks to the
// centre and stays scores well on every obedience term without ever reading the
// signal, which is the confound this whole instrument exists to avoid. The
// centre does drift as a run grows the board — that part of the old argument
// was true — but it drifts once a front, which is far too slow to be the
// gradient the head is being asked to track.
//
// What it is for is the fan-out: resource gathering and marches want a planner
// that searches forward properly, and a search only learns to value "get the
// body of the army over there" if something measures whether it did.

import { fileOf, rankOf, square, type Side } from "./board";
import { hasCell, type Position } from "./position";
import { streamFor } from "./random";

/**
 * The three lights, and their periods.
 *
 * **Three independent instructions, not one with three names.** The trainer
 * tells its army three things at once — work the patch, press the enemy or keep
 * away, and whether it may take anything at all — and every pair of them has to
 * be separable or the whole instrument is worthless: a fitness that cannot tell
 * obedience to one from obedience to another will reward whichever is cheapest,
 * and the head will learn that instead.
 *
 * So the periods stay **pairwise co-prime**, and that matters more the longer
 * they get, not less: equal periods flip together and the four lights collapse
 * into one instruction with four names. Co-prime is what keeps them
 * *overlapping* — every combination reachable, none predicting another.
 *
 * The overlap is the point rather than a side effect. *Harvest while
 * attacking*, *go there while harvesting*, *hold position and take nothing* are
 * the squad objectives §13.4 wants, and an army trained across the whole space
 * has been taught it rather than four lines through it.
 *
 * **About a fifth of a game each**, which is a deliberate move away from 5, 6, 7
 * and 11. The rig plays this profile over 128 plies — 64 turns — so a window of
 * thirteen-ish turns is roughly 20% of a game.
 *
 * The reason is that a short window asks for something an army cannot do. A
 * harvest light can be obeyed on the next turn, but saving for a recipe takes
 * several, and a march across a grown board takes more — so at a period of five
 * the instruction was over before the act it asks for could finish, and what
 * got scored was luck. A fifth of a game is long enough to organise around.
 *
 * **What it costs is combinations per game, and what saves it is the seed.**
 * Sixteen combinations exist and a 64-turn game at these periods sees perhaps a
 * third of them — where the old set showed nearly all of them every game. That
 * would be fatal if a batch were one game. It is not: the offset below is drawn
 * from the *game's own seed*, so sixteen games start their windows in sixteen
 * different places and the batch covers the space even though no single game
 * does. Per-game coverage fell; per-batch coverage did not.
 *
 * **Whole turns, not plies.** A light that flipped every half-move is noise: an
 * army cannot start an errand and stop it inside one exchange, and what is being
 * taught is that the *state* of a light is worth conditioning on.
 */
export const TRAINER_PHASE = {
  /** Whether digging counts. The resource light. */
  harvest: 13,
  /** Press toward the enemy, or keep away. */
  posture: 15,
  /**
   * Whether it may take anything at all.
   *
   * The shortest, because it is the fastest instruction to obey: a permission
   * is honoured or broken on the very next move, where every other light here
   * asks for something that takes turns to arrange.
   */
  attack: 11,
  /**
   * Whether it should be spending metal on pieces. The forge light.
   *
   * **Seventeen, and the co-primality is not a decoration.** 11, 13, 15 and 17
   * are pairwise co-prime — 15 is 3x5 and shares a factor with none of the
   * others — so the full cycle is 36,465 turns, which no game will ever reach.
   * Two lights that lined up would leave the fitness unable to tell obedience
   * to one from obedience to the other, which is the whole instrument gone.
   *
   * **The longest period, because a forge is the slowest instruction.** Saving
   * for a recipe takes several turns and a light that flickered faster than the
   * act it asks for would score an army for luck. That argument is what drove
   * every period up, not just this one.
   */
  forge: 17,
  /**
   * Chess or factory: which game the army is playing this stretch. The mode
   * light (§32 L1).
   *
   * **Thirty-one, and long on purpose.** The other four flicker because what
   * they teach is timing inside one plan; this one says what the *plan* is —
   * play to win the chess game, or maximise throughput — and both take many
   * turns to show: a mating net or a material swing is a campaign, and ore dug,
   * cooked on a coal burn and forged is a chain several ticks long. So it is
   * about half a 64-turn game and a third of the rig's 90-turn one: every game
   * longer than 31 turns sees both modes, and no stretch is shorter than the
   * act it asks for.
   *
   * **Prime, so co-prime with 11, 13, 15, 17 and with `MARK_HOP_TURNS` (2)**:
   * no other light lines up with it, and the full cycle (1,130,415 turns) is
   * past any game.
   */
  mode: 31
} as const;

/**
 * A salt each, so three schedules are three draws rather than one thrice.
 *
 * Distinct primes for the reason `styleForStage` takes its own stream: a draw
 * slipped into somebody else's sequence changes every existing seed's results
 * as a side effect, and the failure is silent and total.
 */
const TRAINER_SALT = { harvest: 61, posture: 67, attack: 71, forge: 73, mode: 83 } as const;

/**
 * One light, on its own clock.
 *
 * **Seeded offset, fixed period.** Irregular across matches so an army cannot
 * learn "green on turn seven" — that would be learning the rig rather than the
 * game — and regular within one so it stays cheap, because these are asked from
 * `evaluate` and a walked schedule would be a loop per call.
 */
function light(position: Position, seed: number, of: keyof typeof TRAINER_PHASE): boolean {
  const phase = TRAINER_PHASE[of];
  const offset = Math.floor(streamFor(seed, 0, TRAINER_SALT[of])() * phase * 2);
  return Math.floor((position.fullmove + offset) / phase) % 2 === 0;
}

/**
 * Is ore gained right now worth anything?
 *
 * The resource light. What it teaches is not "dig" — one coefficient can say
 * that — but "dig *now*", which is the same board meaning two different things
 * depending on a signal, and the shape no linear term can hold.
 */
export const trainerHarvest = (position: Position, seed: number): boolean =>
  light(position, seed, "harvest");

/**
 * Press toward the enemy, or keep away from it?
 *
 * A posture rather than a target: "close on them" and "keep off them" are the
 * two things an army can be told about an enemy it can already see, and
 * `foeNear` is what it sees them with. This says which way that number is
 * supposed to point.
 */
export const trainerOffensive = (position: Position, seed: number): boolean =>
  light(position, seed, "posture");

/**
 * May it take anything at all?
 *
 * **A permission, and deliberately not the same question as the posture.** One
 * says where to stand and the other says what may be done from there — which is
 * the difference between the march ladder's Hold and its Engage, and the pair
 * covers that ladder plus a direction. An army pressed forward *and* forbidden
 * to take is being told to threaten, which is a real instruction and one no
 * single flag could give.
 */
export const trainerAttack = (position: Position, seed: number): boolean =>
  light(position, seed, "attack");

/**
 * Should it be turning metal into pieces?
 *
 * **The instruction a harvesting party cannot do without.** A crew is bounded
 * by how many of it can dig, and the only way an army gets another digger is to
 * make one — so "forge pawns" is not an aside for this profile, it is its
 * signature act, and a minted pawn arrives facing away from whatever made it
 * (`defaultMintFacing`), which is the knight's re-aim asked for by the board
 * rather than by a rule. Whether that comes out as a taught behaviour or as
 * something the search stumbles on is exactly the kind of question this
 * instrument exists to answer.
 *
 * **It needs an input of its own, unlike the harvest light.** That one is
 * carried by the mark going quiet — a target that is there or is not — and a
 * forge order has nowhere to point: it is a yes or a no about the whole army.
 * See `SIGNAL_FORGE`.
 */
export const trainerForge = (position: Position, seed: number): boolean =>
  light(position, seed, "forge");

/**
 * Which game the army is told to play this stretch: chess or factory.
 *
 * **Chess** is play to win the chess game — material, king safety, the mate.
 * **Factory** is industrial throughput: resources in plus resources out (dug,
 * refined, forged, spent on builds). One light, two named states, so the
 * fitness scores each turn by the half it was asked for and acceptance reads
 * the halves apart. Rides `Position.signals` as `SIGNAL_MODE_CHESS` /
 * `SIGNAL_MODE_FACTORY`, like the forge light.
 */
export const trainerMode = (position: Position, seed: number): "chess" | "factory" =>
  light(position, seed, "mode") ? "chess" : "factory";

/**
 * The mark itself: the centre of the board's bounds.
 *
 * **Bounds rather than the centroid of `cellList`**, and the difference is
 * whether a ragged board pulls the mark toward its fat end. It should not: what
 * this measures is whether the army is *central*, and the middle of the
 * rectangle everything is inside is the reading a player would give.
 *
 * Falls to the nearest existing cell when the exact middle is a hole — a
 * structure takes a square out of `cells`, and a target nothing can stand on is
 * a gradient with a pit at the bottom of it.
 */
/**
 * How often the mark moves, in whole turns.
 *
 * **Two**, and it is co-prime with all four light periods (11, 13, 15, 17), which
 * it was not when harvest ran on six. That mattered: a cadence dividing the
 * light's period puts exactly the same number of hops in every green window, in
 * every game, which is a regularity an army could learn instead of the lesson.
 * With an odd period the count varies by where the window falls. Keep it
 * co-prime with all four if this number ever moves.
 *
 * A green window is now about thirteen turns, so the mark takes roughly six
 * steps across one — long enough to be followed rather than chased.
 */
export const MARK_HOP_TURNS = 2;

/** Its own draw, so the walk is not sharing a sequence with a light. */
const MARK_SALT = 79;

/** How far the walk will ever be asked to run, so a long game cannot crawl. */
const MAX_HOPS = 512;

/**
 * The last walk, so `evaluate` pays for it once a turn rather than once a call.
 *
 * **A pure memo, not carried state.** Same seed, same step count, same board
 * shape, same answer — it changes no result and breaks no replay, which is the
 * distinction that matters for `src/engine/rules/`. It exists because this is asked
 * from the hottest function the planner has and the walk mints a seeded stream
 * per step: without it, a hundred candidate moves pay for the same twenty draws
 * a hundred times.
 */
let lastWalk: { key: string; square: number } | null = null;

/** The four steps a mark may take, in the order `DIR_OFFSET` lists them. */
const HOPS: readonly [number, number][] = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0]
];

export function trainerMark(position: Position, seed = 0): number {
  const start = centreOf(position);
  if (start < 0 || seed === 0) return start;

  const steps = Math.min(MAX_HOPS, Math.floor(position.fullmove / MARK_HOP_TURNS));
  const { minFile, maxFile, minRank, maxRank } = position.bounds;
  const key = `${seed}:${steps}:${minFile},${maxFile},${minRank},${maxRank}`;
  if (lastWalk && lastWalk.key === key) return lastWalk.square;

  let at = start;
  for (let step = 0; step < steps; step++) {
    // **Chosen from the steps that stay on the board, rather than clamped.**
    // Clamping parks the mark in a corner and the instrument degenerates back
    // into the constant it was — which is the whole thing being fixed. Picking
    // among the legal directions keeps it moving everywhere, and on a ragged
    // board it also keeps it on ground that exists.
    const roll = streamFor(seed, step, MARK_SALT)();
    const file = fileOf(at);
    const rank = rankOf(at);
    const open: number[] = [];
    for (const [df, dr] of HOPS) {
      const next = square(file + df, rank + dr);
      if (hasCell(position, next)) open.push(next);
    }
    if (open.length === 0) break;
    at = open[Math.floor(roll * open.length) % open.length];
  }
  lastWalk = { key, square: at };
  return at;
}

/** The middle of the board's own bounds, or the nearest ground to it. */
function centreOf(position: Position): number {
  const { minFile, maxFile, minRank, maxRank } = position.bounds;
  const midFile = Math.floor((minFile + maxFile) / 2);
  const midRank = Math.floor((minRank + maxRank) / 2);
  const middle = square(midFile, midRank);
  if (hasCell(position, middle)) return middle;

  // Rings outward until it finds ground. Bounded by the board's own size, so a
  // position with no cells at all answers -1 rather than spinning.
  const reach = Math.max(maxFile - minFile, maxRank - minRank);
  for (let r = 1; r <= reach; r++) {
    for (let df = -r; df <= r; df++) {
      for (let dr = -r; dr <= r; dr++) {
        // The ring only, not the filled square — the inside has been looked at.
        if (Math.abs(df) !== r && Math.abs(dr) !== r) continue;
        const sq = square(midFile + df, midRank + dr);
        if (hasCell(position, sq)) return sq;
      }
    }
  }
  return -1;
}

/** What "nowhere near it" is worth, when there is no army or no board. */
export const TRAINER_FAR = 900;

/**
 * How far this army's centre of mass is from the mark.
 *
 * **One function, read by the fitness and by the head.** The goal scores this
 * and `focusNear` shows the network the same number; a search measuring one
 * thing while the network is shown another cannot converge and cannot say why.
 *
 * Centroid rather than the nearest piece, for the reason `foeNear` is: the
 * nearest is one skirmisher, and it would swing the whole reading every time
 * that piece traded itself off.
 */
/**
 * The square this army is being aimed at *right now*: the mark on green,
 * nothing on red.
 *
 * **Red is an absent target rather than a second signal**, and that is the
 * whole trick. The head's aimable slots already say "there is no such thing
 * here" with `NO_TARGET`, and the transform already flattens that to a nought
 * that means *no opinion* — so the light needs no input of its own. Green is a
 * distance to close on; red is the same slot going quiet, and what the head has
 * to learn is that quiet means hold.
 *
 * It also generalises past the trainer, which is the point of building it this
 * way: "pursue the errand while there is one, do not wander when there is not"
 * is the same sentence with `focusNear` fed from a march instead.
 *
 * **A bout is the exception, and it is not wired here yet.** In the royale the
 * lights are all green — they are a teaching device, and a bout is deciding
 * which finalists breed rather than teaching anybody, so an army spending half
 * of one forbidden to attack would be ranked on the handicap. `playGame` takes
 * `unleashed` for the *scoring* half of that today. The other half is this
 * function: when the head's aimable slots are fed from the mark, a bout has to
 * pass green here too, or the network is shown a red light while the fitness is
 * scoring a green one — which is the "measuring one thing and showing another"
 * failure this file's own comments already warn about.
 */
export function trainerTarget(position: Position, seed: number): number {
  return trainerHarvest(position, seed) ? trainerMark(position, seed) : -1;
}

/**
 * The bits `Position.signals` can carry.
 *
 * **Two bits for one light, and that is what makes it a mask rather than a
 * flag.** A single bit has only "set" and "unset", and unset is already the
 * ordinary board — every game that has never heard of the trainer — so a
 * one-bit forge light could say *forge* and could not say *do not forge*
 * without also saying it to everybody. The head has to tell three states apart:
 * green, red, and nobody is giving orders. Held apart here, `forgeSignal` maps
 * them onto the signed input the network reads, which is the same shape `mode`
 * uses and for the same reason.
 */
export const SIGNAL_FORGE_ON = 1;
export const SIGNAL_FORGE_OFF = 2;

/**
 * The relay light: hand what you are holding to the army beside you.
 *
 * **Two bits again, for the reason the forge light needs two.** Absent is every
 * ordinary board in the game and has to stay distinguishable from a standing
 * order *not* to relay, or the head learns that the whole game is a red light.
 *
 * **Held on for a whole operation rather than flickering on a prime.** The
 * other four lights teach *timing* — the same board meaning two things
 * depending on a signal — and they flicker because that is the only way to say
 * "now". This one says what the errand *is*: the board it comes on is built for
 * it, both armies sparse and allied with nobody to fight, so a red half would
 * be a game with nothing to do in it. The conditioning it teaches is against
 * every other game, where the light is absent.
 */
export const SIGNAL_RELAY_ON = 4;
export const SIGNAL_RELAY_OFF = 8;

/**
 * The mode light (§32 L1): play the chess game, or run the factory.
 *
 * **Two bits for the forge light's reason**: absent is every board nobody is
 * giving orders on, and must stay apart from both orders. Both bits set is
 * malformed; `modeSignal` reads chess first.
 *
 * **Two writers, never the reducer.** The tuner writes the trainer's light
 * (`trainerMode`) onto its own board between turns; a shipped game's driver
 * writes the faction's need onto the *planner's copy* of the state
 * (`withModeLight`, `ai/modeLight.ts`), so the store, the log and the hash never
 * see it. No rule reads either bit. Hashed when set, like every signal.
 */
export const SIGNAL_MODE_CHESS = 16;
export const SIGNAL_MODE_FACTORY = 32;
/** Both mode bits, for clearing one before writing the other. */
export const SIGNAL_MODE_MASK = SIGNAL_MODE_CHESS | SIGNAL_MODE_FACTORY;

/** The bit that says `mode`. */
export const modeBit = (mode: "chess" | "factory"): number =>
  mode === "chess" ? SIGNAL_MODE_CHESS : SIGNAL_MODE_FACTORY;

/**
 * The mode light as the head and the evaluation read it: +1 chess, -1
 * factory, 0 nobody said — signed as `forgeSignal` is, nought the ordinary
 * board.
 */
export function modeSignal(position: Position): number {
  if (position.signals & SIGNAL_MODE_CHESS) return 1;
  if (position.signals & SIGNAL_MODE_FACTORY) return -1;
  return 0;
}

/** The mask a trainer board carries this turn: the forge and mode lights, two bits each. */
export function trainerSignals(position: Position, seed: number): number {
  return (
    (trainerForge(position, seed) ? SIGNAL_FORGE_ON : SIGNAL_FORGE_OFF) |
    modeBit(trainerMode(position, seed))
  );
}

/**
 * The relay light as the head and the evaluation read it: +1, -1, 0 for unsaid.
 *
 * Signed exactly as `forgeSignal` is, and nought is "no orders" for the same
 * reason — it is by far the commonest value this will ever hold.
 */
export function relaySignal(position: Position): number {
  if (position.signals & SIGNAL_RELAY_ON) return 1;
  if (position.signals & SIGNAL_RELAY_OFF) return -1;
  return 0;
}

/**
 * Who "the allied army" is, for a relay.
 *
 * **One definition, because the evaluation and the fitness both ask.** The
 * planner is paid to walk material toward these sides and the rig scores what
 * lands in their hands; two readings of which sides those are would let a
 * search climb one thing while being measured on another, which is the failure
 * `laneDelivers` is one function to avoid. It is also the reason this lives in
 * the rules rather than in either caller.
 *
 * **Allies other than yourself.** Every side is its own ally
 * (`alliances[s] === s` by default), so the obvious `!areEnemies` reading
 * counts the mover's own hands as delivered — which would score an army for
 * picking ore up and never passing it on. Empty on every ordinary board, which
 * is what keeps the whole mechanic inert outside an operation.
 */
export function relayAllies(position: Position, side: Side): Side[] {
  const out: Side[] = [];
  for (let other = 0; other < position.sides; other++) {
    if (other === side) continue;
    if (position.alliances[other] === position.alliances[side]) out.push(other as Side);
  }
  return out;
}

/**
 * The forge light as the head reads it: +1 green, -1 red, 0 nobody said.
 *
 * **Nought is "no orders", never "do not forge".** Every ordinary board in the
 * game carries no signals at all, so the absent case is by far the commonest
 * one this slot will ever see — and reading it as a standing instruction not to
 * forge would teach the network that the whole game is a red light with a few
 * green flashes in it. Signed for the reason `mode` is: both halves of `tanh`
 * get used and neither instruction is the one that looks like an empty slot.
 */
export function forgeSignal(position: Position): number {
  if (position.signals & SIGNAL_FORGE_ON) return 1;
  if (position.signals & SIGNAL_FORGE_OFF) return -1;
  return 0;
}

export function trainerDistance(position: Position, side: Side, seed: number): number {
  let file = 0;
  let rank = 0;
  let count = 0;
  for (const [sq, piece] of position.board) {
    if ((piece >> 3) !== side) continue;
    file += fileOf(sq);
    rank += rankOf(sq);
    count++;
  }
  if (count === 0) return TRAINER_FAR;
  const mark = trainerTarget(position, seed);
  if (mark < 0) return TRAINER_FAR;
  return Math.max(
    Math.abs(fileOf(mark) - file / count),
    Math.abs(rankOf(mark) - rank / count)
  );
}

/**
 * Where the army's centre of mass is, as a square.
 *
 * **For the red half of the light**, which is scored on the army *not moving*
 * rather than on where it is — so the fitness differences this between turns
 * and needs it whether or not there is a mark to be near. Rounded to a square
 * so a piece shuffling on the spot reads as nothing happening, which is what
 * standing still means to a person watching.
 */
/**
 * How densely an army fills the ground it occupies: pieces over the cells its
 * own bounding box covers, 0..1.
 *
 * **A density, where `trainerSpread` is a distance, and they disagree.** Spread
 * asks how far the pieces are from a point; this asks how much of the space
 * between them is *filled*. A ring and a solid block can have the same spread
 * and opposite packing; a long thin line is spread out and, in its own narrow
 * box, tightly packed. Neither can be derived from the other, which is the
 * whole reason this is a second reading rather than a rescaling of the first.
 *
 * **Counted against cells that exist, not against the rectangle.** A board that
 * has grown ragged has holes in the middle of any box drawn on it, and counting
 * those as space the army failed to fill would read a formation as loose for
 * standing next to a structure. `hasCell` is the same test movement already
 * makes.
 *
 * A lone piece is a box of one and answers a perfectly dense **1** — which is
 * correct rather than a corner to guard: an army of one *is* as packed as an
 * army can be, and a goal aiming at anything looser charges it accordingly.
 * An empty army has no box and answers 0, the only value that says nothing.
 */
export function armyPacking(position: Position, side: Side): number {
  let minFile = Infinity;
  let maxFile = -Infinity;
  let minRank = Infinity;
  let maxRank = -Infinity;
  let count = 0;
  for (const [sq, piece] of position.board) {
    if ((piece >> 3) !== side) continue;
    const file = fileOf(sq);
    const rank = rankOf(sq);
    if (file < minFile) minFile = file;
    if (file > maxFile) maxFile = file;
    if (rank < minRank) minRank = rank;
    if (rank > maxRank) maxRank = rank;
    count++;
  }
  if (count === 0) return 0;

  let room = 0;
  for (let rank = minRank; rank <= maxRank; rank++) {
    for (let file = minFile; file <= maxFile; file++) {
      if (hasCell(position, square(file, rank))) room++;
    }
  }
  return room === 0 ? 0 : Math.min(1, count / room);
}

/**
 * How near an army's packing is to the density it was told to hold, 0..1.
 *
 * **A tent, deliberately, and not a squared error or a threshold.** What this
 * has to be is *climbable from both sides*: a slope of one everywhere means a
 * formation that is too tight and one that is too loose both know which way to
 * move, and an army that is already right has something to lose by drifting. A
 * squared error flattens out exactly where the search spends most of its time —
 * near the target — and a threshold has no gradient at all, which is the
 * `greenDigsPerTurn` failure written as a shape.
 *
 * `target` is a fraction. Nought is nonsense and one is "pack as tight as you
 * can", which is the arrangement worth having a knob to argue with.
 */
export function packingReward(packing: number, target: number): number {
  return Math.max(0, 1 - Math.abs(packing - target));
}

export function trainerCentroid(position: Position, side: Side): number {
  let file = 0;
  let rank = 0;
  let count = 0;
  for (const [sq, piece] of position.board) {
    if ((piece >> 3) !== side) continue;
    file += fileOf(sq);
    rank += rankOf(sq);
    count++;
  }
  if (count === 0) return -1;
  return square(Math.round(file / count), Math.round(rank / count));
}

/**
 * How tightly the army is gathered around the mark.
 *
 * The mean of each piece's own distance against `trainerDistance`'s single
 * centroid reading — the second of the two aimable slots, and a different
 * question: *where* the force is, and whether it is a force or a smear. An army
 * straddling the middle has a small centroid distance and a large spread.
 */
export function trainerSpread(position: Position, side: Side, seed: number): number {
  const mark = trainerTarget(position, seed);
  if (mark < 0) return TRAINER_FAR;
  let total = 0;
  let count = 0;
  for (const [sq, piece] of position.board) {
    if ((piece >> 3) !== side) continue;
    total += Math.max(Math.abs(fileOf(mark) - fileOf(sq)), Math.abs(rankOf(mark) - rankOf(sq)));
    count++;
  }
  return count === 0 ? TRAINER_FAR : total / count;
}
